import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';

import { ENV } from '../../config/env';

// Types for company data matching backend DTOs
interface SocialLink {
    platform: string;
    url: string;
}

interface Owner {
    userId: string;
    name: string;
    email: string;
}

interface CompanyData {
    id: string;
    name: string;
    industry: string;
    website: string;
    description: string;
    logoUrl: string;
    createdBy: string;
    owner: Owner;
    isActive: boolean;
    profileStrength: number;
    // New profile fields
    phone: string;
    headquarters: string;
    companySize: string;
    foundedYear: string;
    aboutMe: string;
    socialLinks: SocialLink[];
    specialties: string[];
    hiringPreferences: string[];
}

interface EditableFields {
    name: string;
    industry: string;
    website: string;
    description: string;
    phone: string;
    headquarters: string;
    companySize: string;
    foundedYear: string;
    aboutMe: string;
    socialLinks: SocialLink[];
    specialties: string[];
    hiringPreferences: string[];
}

// Available options for dropdowns
const COMPANY_SIZES = [
    '1-10',
    '11-50',
    '51-100',
    '101-300',
    '301-500',
    '501-1000',
    '1000+'
];

const INDUSTRIES = [
    'Technology',
    'Finance & Accounting',
    'Healthcare',
    'Education',
    'Manufacturing',
    'Retail',
    'Marketing & Communications',
    'Legal & Compliance',
    'Human Resources',
    'Design & Creative',
    'Data & Analytics',
    'Operations & Strategy',
    'Engineering & Development',
    'Product Management',
    'Other'
];


const SOCIAL_PLATFORMS = [
    'LinkedIn',
    'Twitter',
    'Instagram',
    'Facebook',
    'Dribbble',
    'Behance',
    'GitHub',
    'YouTube',
    'TikTok'
];

/**
 * Company Profile Page
 * Displays editable company profile for authenticated company users
 */
const CompanyProfile: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [companyData, setCompanyData] = useState<CompanyData | null>(null);
    const [editableFields, setEditableFields] = useState<EditableFields>({
        name: '',
        industry: '',
        website: '',
        description: '',
        phone: '',
        headquarters: '',
        companySize: '',
        foundedYear: '',
        aboutMe: '',
        socialLinks: [],
        specialties: [],
        hiringPreferences: []
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    // New social link input state
    const [newSocialPlatform, setNewSocialPlatform] = useState('');
    const [newSocialUrl, setNewSocialUrl] = useState('');


    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('authToken');
        const user = localStorage.getItem('authUser');

        if (!token || !user) {
            navigate('/auth/login', { replace: true });
            return;
        }

        // Fetch company data
        fetchCompanyData();
    }, [navigate]);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            setError('');

            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token');
            }

            // First try to get company from localStorage
            const storedCompany = localStorage.getItem('companyData');
            let companyId = '';

            if (storedCompany) {
                const parsed = JSON.parse(storedCompany);
                companyId = parsed.id || parsed._id;
            }

            // If no company ID in localStorage, fetch from my-companies endpoint
            if (!companyId) {
                const myCompaniesResponse = await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/my-companies`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!myCompaniesResponse.ok) {
                    throw new Error('Failed to fetch companies');
                }

                const myCompaniesData = await myCompaniesResponse.json();
                const companies = myCompaniesData.payload || myCompaniesData.data || myCompaniesData || [];

                if (Array.isArray(companies) && companies.length > 0) {
                    companyId = companies[0].id || companies[0]._id;
                    localStorage.setItem('companyData', JSON.stringify(companies[0]));
                } else {
                    throw new Error('No company found for this user');
                }
            }

            // Fetch full company details
            const response = await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch company details');
            }

            const data = await response.json();
            const company = data.payload || data;

            setCompanyData(company);
            setEditableFields({
                name: company.name || '',
                industry: company.industry || '',
                website: company.website || '',
                description: company.description || '',
                phone: company.phone || '',
                headquarters: company.headquarters || '',
                companySize: company.companySize || '',
                foundedYear: company.foundedYear || '',
                aboutMe: company.aboutMe || '',
                socialLinks: company.socialLinks || [],
                specialties: company.specialties || [],
                hiringPreferences: company.hiringPreferences || []
            });

            // Update localStorage
            localStorage.setItem('companyData', JSON.stringify(company));

        } catch (err) {
            console.error('Failed to fetch company data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load company data');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        if (!companyData) return;

        try {
            setSaving(true);
            setError('');
            setSuccessMessage('');

            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token');
            }

            const response = await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/${companyData.id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editableFields),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.status?.msg || 'Failed to update profile');
            }

            const data = await response.json();
            const updatedCompany = data.payload || data;

            setCompanyData(updatedCompany);
            localStorage.setItem('companyData', JSON.stringify(updatedCompany));
            setSuccessMessage('Profile updated successfully!');
            setIsEditing(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            console.error('Failed to save profile:', err);
            setError(err instanceof Error ? err.message : 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !companyData) return;

        // For now, we'll just show a preview - actual upload would need a file upload endpoint
        setUploadingLogo(true);

        try {
            // Create a preview URL
            const previewUrl = URL.createObjectURL(file);

            // Update local state with preview
            setCompanyData(prev => prev ? { ...prev, logoUrl: previewUrl } : null);

            // TODO: Implement actual file upload when backend endpoint is available
            // const formData = new FormData();
            // formData.append('logo', file);
            // await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/${companyData.id}/logo`, ...);

            setSuccessMessage('Logo preview updated. Save changes to upload.');
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            setError('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleAddSocialLink = () => {
        if (!newSocialPlatform || !newSocialUrl) return;

        const newLink: SocialLink = {
            platform: newSocialPlatform,
            url: newSocialUrl
        };

        setEditableFields(prev => ({
            ...prev,
            socialLinks: [...prev.socialLinks, newLink]
        }));

        setNewSocialPlatform('');
        setNewSocialUrl('');
    };

    const handleRemoveSocialLink = (index: number) => {
        setEditableFields(prev => ({
            ...prev,
            socialLinks: prev.socialLinks.filter((_, i) => i !== index)
        }));
    };


    const handleCancelEdit = () => {
        if (companyData) {
            setEditableFields({
                name: companyData.name || '',
                industry: companyData.industry || '',
                website: companyData.website || '',
                description: companyData.description || '',
                phone: companyData.phone || '',
                headquarters: companyData.headquarters || '',
                companySize: companyData.companySize || '',
                foundedYear: companyData.foundedYear || '',
                aboutMe: companyData.aboutMe || '',
                socialLinks: companyData.socialLinks || [],
                specialties: companyData.specialties || [],
                hiringPreferences: companyData.hiringPreferences || []
            });
        }
        setIsEditing(false);
    };

    // Generate years for founded year dropdown
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => String(currentYear - i));

    if (loading) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium text-gray-500 font-['Manrope']">Loading profile...</p>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    if (error && !companyData) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="text-lg font-medium text-red-600 font-['Manrope']">{error}</p>
                        <button
                            onClick={fetchCompanyData}
                            className="mt-4 px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a82] transition-colors font-['Manrope']"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    return (
        <CompanyLayout>
            <div className="space-y-6">
                {/* Success/Error Messages */}
                {successMessage && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="text-green-700 font-['Manrope'] text-sm">{successMessage}</span>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <span className="text-red-700 font-['Manrope'] text-sm">{error}</span>
                    </div>
                )}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Main Profile Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Profile Header Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex items-center gap-4">
                                    {/* Company Logo */}
                                    <div
                                        className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group"
                                        onClick={() => isEditing && fileInputRef.current?.click()}
                                    >
                                        {companyData?.logoUrl ? (
                                            <img
                                                src={companyData.logoUrl}
                                                alt={companyData.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-[#015FA6] to-[#0172C6] flex items-center justify-center text-white text-3xl font-bold">
                                                {companyData?.name?.charAt(0) || 'C'}
                                            </div>
                                        )}
                                        {isEditing && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="white"/>
                                                </svg>
                                            </div>
                                        )}
                                        {uploadingLogo && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        className="hidden"
                                    />

                                    <div>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={editableFields.name}
                                                onChange={(e) => setEditableFields(prev => ({ ...prev, name: e.target.value }))}
                                                className="text-2xl font-bold text-[#0B111E] bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 font-['Manrope'] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                                placeholder="Company Name"
                                            />
                                        ) : (
                                            <h1 className="text-2xl font-bold text-[#0B111E] font-['Manrope']">
                                                {companyData?.name || 'Company Name'}
                                            </h1>
                                        )}
                                        <p className="text-sm text-[#7C8493] font-['Manrope'] mt-1">
                                            {companyData?.owner?.email || 'email@company.com'}
                                        </p>
                                    </div>
                                </div>

                                {/* Edit/Save Buttons */}
                                <div className="flex items-center gap-3">
                                    {isEditing ? (
                                        <>
                                            <button
                                                onClick={handleCancelEdit}
                                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-['Manrope'] text-sm font-medium transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveProfile}
                                                disabled={saving}
                                                className="px-4 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a82] font-['Manrope'] text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                                            >
                                                {saving && (
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                )}
                                                Save Changes
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="px-4 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a82] font-['Manrope'] text-sm font-medium transition-colors flex items-center gap-2"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="currentColor"/>
                                            </svg>
                                            Edit Profile
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Company Information Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-bold text-[#0B111E] font-['Manrope'] flex items-center gap-2">
                                    <div className="w-6 h-6 bg-[#015FA6] rounded flex items-center justify-center">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M19 21V5C19 3.9 18.1 3 17 3H7C5.9 3 5 3.9 5 5V21L12 18L19 21Z" fill="white"/>
                                        </svg>
                                    </div>
                                    Company Information
                                </h2>
                                {!isEditing && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="p-2 text-[#015FA6] hover:bg-blue-50 rounded-full transition-colors"
                                        title="Edit Company Information"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Company Name */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Company Name</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editableFields.name}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                        />
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.name || '-'}</p>
                                    )}
                                </div>

                                {/* Industry */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Industry</label>
                                    {isEditing ? (
                                        <select
                                            value={editableFields.industry}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, industry: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                        >
                                            <option value="">Select Industry</option>
                                            {INDUSTRIES.map(ind => (
                                                <option key={ind} value={ind}>{ind}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.industry || '-'}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Email</label>
                                    <p className="text-[#0B111E] font-['Manrope']">{companyData?.owner?.email || '-'}</p>
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Mobile</label>
                                    {isEditing ? (
                                        <input
                                            type="tel"
                                            value={editableFields.phone}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, phone: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                            placeholder="+1 234 567 8900"
                                        />
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.phone || '-'}</p>
                                    )}
                                </div>

                                {/* Headquarters */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Headquarters</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editableFields.headquarters}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, headquarters: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                            placeholder="New York, USA"
                                        />
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.headquarters || '-'}</p>
                                    )}
                                </div>

                                {/* Company Size */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Company Size</label>
                                    {isEditing ? (
                                        <select
                                            value={editableFields.companySize}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, companySize: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                        >
                                            <option value="">Select Size</option>
                                            {COMPANY_SIZES.map(size => (
                                                <option key={size} value={size}>{size} employees</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.companySize ? `${companyData.companySize} employees` : '-'}</p>
                                    )}
                                </div>

                                {/* Founded */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Founded</label>
                                    {isEditing ? (
                                        <select
                                            value={editableFields.foundedYear}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, foundedYear: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                        >
                                            <option value="">Select Year</option>
                                            {years.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="text-[#0B111E] font-['Manrope']">{companyData?.foundedYear || '-'}</p>
                                    )}
                                </div>

                                {/* Website */}
                                <div>
                                    <label className="block text-sm font-medium text-[#7C8493] font-['Manrope'] mb-2">Website</label>
                                    {isEditing ? (
                                        <input
                                            type="url"
                                            value={editableFields.website}
                                            onChange={(e) => setEditableFields(prev => ({ ...prev, website: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                            placeholder="https://company.com"
                                        />
                                    ) : (
                                        <a
                                            href={companyData?.website?.startsWith('http') ? companyData.website : `https://${companyData?.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[#015FA6] hover:underline font-['Manrope']"
                                        >
                                            {companyData?.website || '-'}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* About Me Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-[#0B111E] font-['Manrope'] flex items-center gap-2">
                                    <div className="w-6 h-6 bg-[#015FA6] rounded flex items-center justify-center">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="white"/>
                                        </svg>
                                    </div>
                                    About me
                                </h2>
                                {!isEditing && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="p-2 text-[#015FA6] hover:bg-blue-50 rounded-full transition-colors"
                                        title="Edit About Me"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                            {isEditing ? (
                                <textarea
                                    value={editableFields.aboutMe}
                                    onChange={(e) => setEditableFields(prev => ({ ...prev, aboutMe: e.target.value }))}
                                    rows={4}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6] resize-none"
                                    placeholder="Tell us about yourself and your role at the company..."
                                />
                            ) : (
                                <p className="text-[#4D5761] font-['Manrope'] leading-relaxed">
                                    {companyData?.aboutMe || 'No information provided yet.'}
                                </p>
                            )}
                        </div>

                        {/* About the Company Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-[#0B111E] font-['Manrope'] flex items-center gap-2">
                                    <div className="w-6 h-6 bg-[#015FA6] rounded flex items-center justify-center">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V17H14V15H12V13H14V11H12V9H20V19ZM18 11H16V13H18V11ZM18 15H16V17H18V15Z" fill="white"/>
                                        </svg>
                                    </div>
                                    About the Company
                                </h2>
                                {!isEditing && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="p-2 text-[#015FA6] hover:bg-blue-50 rounded-full transition-colors"
                                        title="Edit About the Company"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                            {isEditing ? (
                                <textarea
                                    value={editableFields.description}
                                    onChange={(e) => setEditableFields(prev => ({ ...prev, description: e.target.value }))}
                                    rows={6}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6] resize-none"
                                    placeholder="Describe your company, its mission, values, and what makes it unique..."
                                />
                            ) : (
                                <p className="text-[#4D5761] font-['Manrope'] leading-relaxed">
                                    {companyData?.description || 'No company description provided yet.'}
                                </p>
                            )}
                        </div>

                        {/* Links (Social Links) Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-[#0B111E] font-['Manrope'] flex items-center gap-2">
                                    <div className="w-6 h-6 bg-[#015FA6] rounded flex items-center justify-center">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                            <path d="M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z" fill="white"/>
                                        </svg>
                                    </div>
                                    Links
                                </h2>
                                {!isEditing && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="p-2 text-[#015FA6] hover:bg-blue-50 rounded-full transition-colors"
                                        title="Edit Links"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M3 17V21H7L17.8 10.2L13.8 6.2L3 17ZM21.7 7.3C22.1 6.9 22.1 6.3 21.7 5.9L18.1 2.3C17.7 1.9 17.1 1.9 16.7 2.3L14.7 4.3L18.7 8.3L21.7 7.3Z" fill="currentColor"/>
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {/* Existing Social Links */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                {(isEditing ? editableFields.socialLinks : companyData?.socialLinks || []).map((link, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2 bg-[#F0F5FF] text-[#015FA6] px-3 py-2 rounded-lg font-['Manrope'] text-sm"
                                    >
                                        <span className="font-medium">{link.platform}</span>
                                        {isEditing && (
                                            <button
                                                onClick={() => handleRemoveSocialLink(index)}
                                                className="hover:bg-[#015FA6] hover:text-white rounded-full w-4 h-4 flex items-center justify-center"
                                            >
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {(!isEditing && (!companyData?.socialLinks || companyData.socialLinks.length === 0)) && (
                                    <p className="text-[#7C8493] font-['Manrope'] text-sm">No social links added yet.</p>
                                )}
                            </div>

                            {/* Add New Social Link */}
                            {isEditing && (
                                <div className="flex gap-3">
                                    <select
                                        value={newSocialPlatform}
                                        onChange={(e) => setNewSocialPlatform(e.target.value)}
                                        className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                    >
                                        <option value="">Select Platform</option>
                                        {SOCIAL_PLATFORMS.map(platform => (
                                            <option key={platform} value={platform}>{platform}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="url"
                                        value={newSocialUrl}
                                        onChange={(e) => setNewSocialUrl(e.target.value)}
                                        placeholder="https://..."
                                        className="flex-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl font-['Manrope'] text-[#0B111E] focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                                    />
                                    <button
                                        onClick={handleAddSocialLink}
                                        disabled={!newSocialPlatform || !newSocialUrl}
                                        className="px-4 py-2 bg-[#015FA6] text-white rounded-xl font-['Manrope'] text-sm font-medium hover:bg-[#014a82] disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Add
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Right Column - Sidebar */}
                    <div className="space-y-6">

                        {/* Integrations Card (Static) */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-[#0B111E] font-['Manrope'] mb-4">Integrations</h3>
                            <div className="space-y-3">
                                {[
                                    { name: 'Google Meet', icon: '🎥', connected: false },
                                    { name: 'Zoom', icon: '📹', connected: false },
                                    { name: 'Microsoft Teams', icon: '💼', connected: false },
                                    { name: 'Slack', icon: '💬', connected: false },
                                ].map((integration, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">{integration.icon}</span>
                                            <span className="font-['Manrope'] text-sm text-[#0B111E]">{integration.name}</span>
                                        </div>
                                        <button className="text-xs text-[#015FA6] font-medium font-['Manrope'] hover:underline">
                                            Connect
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Brand Assets Card (Static) */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-[#0B111E] font-['Manrope'] mb-4">Brand Assets</h3>
                            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mx-auto mb-3 text-gray-300">
                                    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="currentColor"/>
                                </svg>
                                <p className="text-sm text-[#7C8493] font-['Manrope']">Upload brand assets</p>
                                <p className="text-xs text-[#7C8493] font-['Manrope'] mt-1">Logo, colors, fonts</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </CompanyLayout>
    );
};

export default CompanyProfile;
